import { useEffect, useRef, useState } from "react";
import { FaSpinner } from "react-icons/fa";
import { hideModal, showModal } from "../utils/modalUtility";
import Pagination from "../components/common/Pagination";
import useAxios from "../utils/useAxios";
import config from "../config";

const BASE_URL = config?.apiBaseUrl;

function Gallery() {
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isEditMode, setIsEditMode] = useState(false);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("link"); // link | local

  const axiosAPI = useAxios();

  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const [formData, setFormData] = useState({
    mediaName: "",
    mediaType: "",
    mediaUrl: "",
    thumbnailUrl: "",
  });

  const [uploadedFile, setUploadedFile] = useState(null); // for file input
  // const fileRef = useRef(null);

  const removeFormData = () => {
    setFormData({
      mediaName: "",
      mediaType: "",
      mediaUrl: "",
      thumbnailUrl: "",
    });
    // fileRef.current.value = null;
  };

  const [filters, setFilters] = useState({
    status: "",
    from_date: "",
    to_date: "",
  });

  // Handler to update filters
  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // Get All Notifications
  const getMedia = async () => {
    try {
      const response = await axiosAPI.get(`/api/media/get-media?page=${page}`);
      // console.log(response.data.data);
      setMedia(response.data.data);
      setTotalPages(response?.data?.totalPages);
      setLoading(false);
    } catch (error) {
      console.error(error);
      setLoading(false);
    }
  };

  useEffect(() => {
    getMedia();
  }, [page]);

  // Handle Input Changes
  const handleChange = (e) => {
    const { id, value, files, type } = e.target;

    if (type === "file") {
      setUploadedFile(files[0]); //  store separately
    } else {
      setFormData((prev) => ({
        ...prev,
        [id]: value,
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // ======================
    // COMMON VALIDATION
    // ======================
    if (!formData.mediaType) {
      return window.notifyMessage(
        "Validation Error",
        "Please select media type",
        "warning",
      );
    }

    // ======================
    // TAB BASED VALIDATION
    // ======================
    if (activeTab === "link" && !formData.mediaUrl?.trim()) {
      return window.notifyMessage(
        "Validation Error",
        "Media URL is required",
        "warning",
      );
    }

    if (activeTab === "local" && !uploadedFile && !formData.file_path) {
      return window.notifyMessage(
        "Validation Error",
        "Please upload file",
        "warning",
      );
    }

    setIsLoading(true);

    try {
      const formPayload = new FormData();

      // ====== MEDIA NAME LOGIC ======
      if (activeTab === "local" && uploadedFile) {
        // Local file upload -> use file name
        // formPayload.append("media_name", uploadedFile.name);

        const fileNameWithoutExt = uploadedFile.name.replace(/\.[^/.]+$/, "");
        formPayload.append("media_name", fileNameWithoutExt);
      } else if (activeTab === "link") {
        // Link upload -> random name
        const randomName = `media_${Date.now()}`;
        formPayload.append("media_name", randomName);
      } else {
        // fallback (edit mode without changing file)
        formPayload.append("media_name", formData.mediaName?.trim() || "");
      }

      formPayload.append("media_type", formData.mediaType);

      if (activeTab === "link") {
        formPayload.append("media_url", formData.mediaUrl?.trim() || "");
        formPayload.append(
          "thumbnail_url",
          formData.thumbnailUrl?.trim() || "",
        );
      } else if (activeTab === "local") {
        if (uploadedFile) {
          formPayload.append("file", uploadedFile);
        }
      }

      // DEBUG FormData
      // console.log("===== FormData Contents =====");
      // for (let [key, value] of formPayload.entries()) {
      //   console.log(key, value);
      // }
      // console.log("=============================");

      // ====== API CALL ======
      if (isEditMode) {
        await axiosAPI.put(`/api/media/${currentUserId}`, formPayload, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        window.notifyMessage(
          "Success",
          "Media updated successfully",
          "success",
        );
      } else {
        await axiosAPI.post("/api/media/add-media", formPayload, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        window.notifyMessage("Success", "Media added successfully", "success");
      }

      hideModal("newGalleryModal");
      getMedia();
      removeFormData();
      setIsEditMode(false);
    } catch (error) {
      console.error(error);
      window.notifyMessage(
        "Error",
        error?.response?.data?.message || "Something went wrong",
        "danger",
      );
    } finally {
      setIsLoading(false);
    }
  };

  // Edit Modal Open
  // const openEditModal = (id) => {
  //   setIsEditMode(true);
  //   setCurrentUserId(id);
  //   // console.log(id);

  //   // Find the notification with this ID and populate the form
  //   const selectedMedia = media.find((media) => media.id === id);
  //   // console.log("Open edit modal", selectedMedia);
  //   if (selectedMedia) {
  //     setFormData({
  //       mediaName: selectedMedia.media_name,
  //       mediaType: selectedMedia.media_type,
  //       mediaUrl: selectedMedia.media_url,
  //       thumbnailUrl: selectedMedia.thumbnail_url,
  //     });
  //   }
  //   // console.log(selectedMedia)
  //   showModal("newGalleryModal");
  // };

  // When edit button is clicked
  const openEditModal = (id) => {
    setCurrentUserId(id);
    const mediaToEdit = media.find((m) => m.id === id);
    if (!mediaToEdit) return;

    setFormData({
      mediaName: mediaToEdit.media_name || "",
      mediaType: mediaToEdit.media_type || "",
      mediaUrl: mediaToEdit.media_url || "",
      thumbnailUrl: mediaToEdit.thumbnail_url || "",
    });

    // Decide which tab to show
    if (mediaToEdit.media_url) {
      setActiveTab("link");
    } else {
      setActiveTab("local");
      setUploadedFile(mediaToEdit.file_path ? mediaToEdit.file_path : null);
    }

    setIsEditMode(true);
    showModal("newGalleryModal");
  };

  const handleDelete = async (id) => {
    // console.log(id);
    const isConfirmed = window.confirm("Are you sure you want to delete this?");
    if (!isConfirmed) return;

    try {
      const response = await axiosAPI.delete(`/api/media/${id}`);
      window.notifyMessage("Success", "Deleted successfully", "success");
      getMedia();
    } catch (error) {
      console.error("Error deleting:", error);
      window.notifyMessage(
        "Error",
        "An error occurred while deleting.",
        "danger",
      );
    }
  };

  // Close Modal
  const closeModal = () => {
    setIsEditMode(false);
    removeFormData();
    setCurrentUserId(null);
  };

  const openAddModal = () => {
    showModal("newGalleryModal");
  };

  // useEffect(() => {
  //   if (activeTab === "link") {
  //     setUploadedFile(null);
  //   }

  //   if (activeTab === "local") {
  //     setFormData((prev) => ({
  //       ...prev,
  //       mediaUrl: "",
  //       thumbnailUrl: "",
  //     }));
  //   }
  // }, [activeTab]);

  useEffect(() => {
    if (activeTab === "link") {
      // Clear uploaded file only
      setUploadedFile(null);
    }

    // Do NOT clear mediaUrl / thumbnailUrl on local tab
    // Let formData remain intact
  }, [activeTab]);

  return (
    <div className="container">
      <div className="page-inner">
        <div className="d-flex justify-content-end align-items-end flex-column flex-md-row">
          <div>
            <button
              type="button"
              className="btn btn-dark btn-round mb-4"
              onClick={() => openAddModal()}
            >
              Add Gallery Item
            </button>
          </div>
        </div>

        {/* Modal */}
        <div
          className="modal fade"
          id="newGalleryModal"
          tabIndex="-1"
          aria-labelledby="newGalleryModalLabel"
          aria-hidden="true"
        >
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title" id="newGalleryModalLabel">
                  {isEditMode ? "Edit Gallery" : "Add Gallery"}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  data-bs-dismiss="modal"
                  aria-label="Close"
                  onClick={closeModal}
                ></button>
              </div>

              <div className="modal-body">
                <div className="modal-body">
                  <form onSubmit={handleSubmit}>
                    {/* Media Name */}
                    <div className="mb-3">
                      <label className="form-label">Media Name</label>
                      <input
                        type="text"
                        className="form-control"
                        id="mediaName"
                        placeholder="Enter media name"
                        value={formData.mediaName}
                        onChange={handleChange}
                        // required
                      />
                    </div>

                    {/* Media Type (COMMON) */}
                    <div className="mb-3">
                      <label className="form-label">Media Type</label>
                      <select
                        className="form-select"
                        id="mediaType"
                        name="mediaType"
                        value={formData.mediaType}
                        onChange={handleChange}
                        required
                      >
                        <option value="" disabled>
                          Select Media Type
                        </option>
                        <option value="image">Image</option>
                        <option value="video">Video</option>
                      </select>
                    </div>

                    {/* Nav Tabs */}
                    <ul className="nav nav-tabs mb-3">
                      <li className="nav-item">
                        <button
                          type="button"
                          className={`nav-link ${activeTab === "link" ? "active" : ""}`}
                          onClick={() => setActiveTab("link")}
                        >
                          Upload via Link
                        </button>
                      </li>

                      <li className="nav-item">
                        <button
                          type="button"
                          className={`nav-link ${activeTab === "local" ? "active" : ""}`}
                          onClick={() => setActiveTab("local")}
                        >
                          Upload Locally
                        </button>
                      </li>
                    </ul>

                    {/* Upload via Link */}
                    {activeTab === "link" && (
                      <>
                        <div className="mb-3">
                          <label className="form-label">Media Url</label>
                          <textarea
                            className="form-control"
                            id="mediaUrl"
                            placeholder="Enter media URL"
                            value={formData.mediaUrl}
                            onChange={handleChange}
                            rows={3}
                            required
                          />
                        </div>

                        <div className="mb-3">
                          <label className="form-label">Thumbnail Url</label>
                          <textarea
                            className="form-control"
                            id="thumbnailUrl"
                            placeholder="Enter thumbnail URL"
                            value={formData.thumbnailUrl}
                            onChange={handleChange}
                            rows={3}
                          />
                        </div>
                      </>
                    )}

                    {/* Upload Locally */}
                    {activeTab === "local" && (
                      <div className="mb-3">
                        <label className="form-label">
                          Upload (Image or Video)
                        </label>
                        <input
                          type="file"
                          className="form-control"
                          id="mediaFile"
                          name="mediaFile"
                          accept="image/*,video/*"
                          onChange={handleChange}
                          required
                        />
                      </div>
                    )}

                    {/* Submit Button */}
                    <button
                      type="submit"
                      className="btn theme-button btn-sm btn-rounded"
                      disabled={isLoading}
                    >
                      {isLoading ? (
                        <FaSpinner className="spinner" spin />
                      ) : isEditMode ? (
                        "Update Gallery"
                      ) : (
                        "Add Gallery"
                      )}
                    </button>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Notification Table */}
        <div className="row">
          <div className="col-md-12">
            <div className="card">
              <div className="card-header d-flex justify-content-between align-items-center flex-wrap gap-3">
                {/* Left Section - User Details */}
                <div className="d-flex align-items-center">
                  <h4 className="card-title mb-0">
                    All Media ({media?.length} )
                  </h4>
                </div>
              </div>
              <div className="card-body">
                <div className="table-responsive">
                  <table className="table table-striped">
                    <thead>
                      <tr>
                        <th>Media Name</th>
                        <th>Media Url</th>
                        <th>Thumbnail Url</th>
                        <th>Media type</th>
                        <th>Created At</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        // Show a loading state when data is being fetched
                        <tr>
                          <td colSpan="6" className="text-center">
                            <div
                              style={{
                                position: "fixed",
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                zIndex: 9999,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              <div
                                className="spinner-border text-success"
                                role="status"
                                style={{ width: "3rem", height: "3rem" }}
                              >
                                <span className="visually-hidden">
                                  Loading...
                                </span>
                              </div>
                            </div>
                          </td>
                        </tr>
                      ) : media.length == 0 ? (
                        // Show a "No Data Found" message when no data is available
                        <tr>
                          <td colSpan="6" className="text-center">
                            No Data Found
                          </td>
                        </tr>
                      ) : (
                        // Render the list of leads if data is available
                        media.map((media) => {
                          const formattedDate = new Date(
                            media.created_at,
                          ).toLocaleDateString("en-GB", {
                            day: "2-digit",
                            month: "short",
                            year: "numeric",
                          });

                          //   Check if the message length is greater than 50 characters and truncate it
                          const truncatedMessage =
                            media?.media_url?.length > 50
                              ? media?.media_url?.slice(0, 50) + "..."
                              : media?.media_url;
                          const truncatedThumbnailMessage =
                            media.thumbnail_url?.length > 50
                              ? media?.thumbnail_url?.slice(0, 50) + "..."
                              : media?.thumbnail_url;
                          return (
                            <tr key={media.id}>
                              <td>{media.media_name}</td>

                              {/* Media Url Column */}
                              <td>
                                {/* Media Url Column */}
                                <td>
                                  {media.media_url || media.file_path ? (
                                    <a
                                      href={
                                        media.media_url ||
                                        `${BASE_URL}${media.file_path}`
                                      }
                                      target="_blank"
                                      rel="noopener noreferrer"
                                    >
                                      <img
                                        src={
                                          media.media_url ||
                                          `${BASE_URL}${media.file_path}`
                                        }
                                        alt={media.media_name}
                                        style={{
                                          width: "50px",
                                          height: "50px",
                                          objectFit: "cover",
                                          borderRadius: "4px",
                                          cursor: "pointer",
                                        }}
                                      />
                                    </a>
                                  ) : (
                                    <div
                                      style={{
                                        width: "50px",
                                        height: "50px",
                                        backgroundColor: "#000",
                                        borderRadius: "4px",
                                      }}
                                      title="No media available"
                                    />
                                  )}
                                </td>
                              </td>

                              <td>
                                {media.thumbnail_url ? (
                                  <a
                                    href={`${media.thumbnail_url}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                  >
                                    <img
                                      src={`${media.thumbnail_url}`}
                                      alt={`${media.media_name} thumbnail`}
                                      style={{
                                        width: "50px",
                                        height: "50px",
                                        objectFit: "cover",
                                        borderRadius: "4px",
                                        cursor: "pointer",
                                      }}
                                    />
                                  </a>
                                ) : (
                                  <span>No thumbnail found</span>
                                )}
                              </td>
                              {/* Media Type */}
                              <td>{media.media_type}</td>

                              {/* Created Date */}
                              <td>{formattedDate}</td>

                              {/* Action Buttons */}
                              <td>
                                <button
                                  className="btn btn-icon btn-round btn-dark btn-sm me-2"
                                  onClick={() => openEditModal(media.id)}
                                >
                                  <i className="fas fa-edit"></i>
                                </button>
                                <button
                                  className="btn btn-icon btn-round btn-danger btn-sm me-2"
                                  onClick={() => handleDelete(media.id)}
                                >
                                  <i className="fas fa-trash"></i>
                                </button>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <Pagination totalPages={totalPages} setPage={setPage} page={page} />
          </div>
        </div>
      </div>
    </div>
  );
}

export default Gallery;
